import { arrayMove } from '@dnd-kit/sortable'
import { EyeOutlined } from '@ant-design/icons'
import {
    Alert,
    Button,
    Divider,
    Flex,
    Modal,
    Space,
    Upload,
    message,
    notification,
} from 'antd'
import Title from 'antd/es/typography/Title'
import { UploadFile as UploadFileInterface } from 'antd/es/upload'
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import PreviewShipmentBlank from '../../../components/buttons/preview-shipment-blank/preview-shipment-blank'
import resolveUploadUrl from '../../../helpers/resolve-upload-url'
import useErrorModal from '../../../hooks/api/use-error-modal'
import useUploadFiles from '../../../hooks/api/use-upload-file'
import {
    formSetsApi,
    useGetFormSetByDocIdQuery,
    useUpdateFormSetMutation,
} from '../../../store/api/form-sets-api'
import { useUpdateShipmentMutation } from '../../../store/api/shipments-api'
import {
    tasksApi,
    useCompleteAssemblyPrepMutation,
    useCompleteAssemblyReworkMutation,
} from '../../../store/api/tasks-api'
import { useAppDispatch } from '../../../store/store'
import Form from '../../../types/api/form'
import FormSet from '../../../types/api/form-set'
import { BLANK_TYPE_BUTTON_COLOR } from '../../../types/api/shipment-blank-type'
import Task from '../../../types/api/task'
import UploadedFile from '../../../types/api/uploaded-file'
import { CreationForm } from './forms-set-orders-editor/components/form-set-editor/components/creation-form/creation-form'
import { FormsTable } from './forms-set-orders-editor/components/form-set-editor/components/forms-table/forms-table'

const EMPTY_FORMS: Form[] = []

interface CompleteAssemblyPrepProps {
    task: Task
}

const CompleteAssemblyPrep: FC<CompleteAssemblyPrepProps> = ({ task }) => {
    const dispatch = useAppDispatch()
    const [open, setOpen] = useState(false)
    const [formSet, setFormSet] = useState(task.shipment?.form_set)
    const [formsOrderIds, setFormsOrderIds] = useState<string[]>([])
    const [blankPreviewFiles, setBlankPreviewFiles] = useState<UploadedFile[]>(
        task.shipment?.blankPreviewFiles ?? []
    )
    const isSavingFileRef = useRef(false)

    const [updateShipment, updateShipmentState] = useUpdateShipmentMutation()
    const [updateFormSet, updateFormSetState] = useUpdateFormSetMutation()
    const [uploadFiles, uploadState] = useUploadFiles()
    const [completePrep, completePrepState] = useCompleteAssemblyPrepMutation()
    const [completeRework, completeReworkState] = useCompleteAssemblyReworkMutation()

    useErrorModal(updateShipmentState, 'Не удалось обновить отгрузку')
    useErrorModal(updateFormSetState, 'Не удалось обновить набор форм')
    useErrorModal(completePrepState, 'Не удалось завершить подготовку сборки')
    useErrorModal(completeReworkState, 'Не удалось завершить внесение правок')

    const isRework = task.type === 'Внесение правок'
    const isLoading =
        uploadState.isLoading ||
        updateShipmentState.isLoading ||
        updateFormSetState.isLoading ||
        completePrepState.isLoading ||
        completeReworkState.isLoading

    const { data: formSetResponse } = useGetFormSetByDocIdQuery(
        [
            formSet?.documentId || '',
            { populate: ['forms', 'forms.form_order', 'assembleFile'] },
        ],
        { skip: !formSet?.documentId }
    )

    const formsFromApi = formSetResponse?.data.forms ?? EMPTY_FORMS
    const assembleFiles = formSetResponse?.data.assembleFile ?? formSet?.assembleFile ?? []

    useEffect(() => {
        const apiIds = formsFromApi.map(f => f.documentId)
        setFormsOrderIds(prev => {
            const kept = prev.filter(id => apiIds.includes(id))
            const added = apiIds.filter(id => !kept.includes(id))
            const next = [...kept, ...added]
            if (next.length === prev.length && next.every((id, i) => id === prev[i])) return prev
            return next
        })
    }, [formsFromApi])

    const forms = useMemo(
        () =>
            formsOrderIds
                .map(id => formsFromApi.find(f => f.documentId === id))
                .filter((f): f is Form => !!f),
        [formsFromApi, formsOrderIds]
    )

    const linkFormSetToShipment = async (nextFormSet: FormSet) => {
        setFormSet(nextFormSet)
        const shipmentDocId = task.shipment?.documentId
        if (!shipmentDocId || task.shipment?.form_set?.documentId === nextFormSet.documentId) {
            return
        }

        await updateShipment({
            docId: shipmentDocId,
            body: {
                data: {
                    form_set: { set: [nextFormSet.documentId] },
                },
            },
        }).unwrap()
    }

    const reorderForms = async (activeId: string, overId: string) => {
        if (!formSet?.documentId) return

        const oldIndex = formsOrderIds.indexOf(activeId)
        const newIndex = formsOrderIds.indexOf(overId)
        if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return

        const previousIds = formsOrderIds
        const nextIds = arrayMove(formsOrderIds, oldIndex, newIndex)
        setFormsOrderIds(nextIds)

        try {
            await updateFormSet({
                docId: formSet.documentId,
                body: {
                    data: {
                        forms: { set: nextIds },
                    },
                },
            }).unwrap()
        } catch {
            setFormsOrderIds(previousIds)
            Modal.error({ title: 'Не удалось сохранить порядок форм в наборе' })
        }
    }

    const saveAssembleFiles = async (fileList: UploadFileInterface[]) => {
        const formSetDocId = formSet?.documentId
        if (!formSetDocId || isSavingFileRef.current) return

        isSavingFileRef.current = true
        try {
            const localFiles = fileList.filter(file => file.originFileObj)
            const existingIds = fileList
                .filter(file => !file.originFileObj)
                .map(file => Number(file.uid))
                .filter(id => !Number.isNaN(id))

            let uploadedIds: number[] = []
            if (localFiles.length) {
                const result = await uploadFiles(localFiles)
                uploadedIds = result.data?.map(file => file.id) ?? []
                if (!uploadedIds.length) {
                    notification.error({ message: 'Не удалось сохранить файл сборки' })
                    return
                }
            }

            const nextIds = [...existingIds, ...uploadedIds]
            await updateFormSet({
                docId: formSetDocId,
                body: {
                    data: {
                        //@ts-ignore
                        assembleFile: nextIds,
                    },
                },
            }).unwrap()

            dispatch(formSetsApi.util.invalidateTags(['FormSet']))
        } finally {
            isSavingFileRef.current = false
        }
    }

    const saveBlankPreviewFiles = async (fileList: UploadFileInterface[]) => {
        const shipmentDocId = task.shipment?.documentId
        if (!shipmentDocId || isSavingFileRef.current) return

        isSavingFileRef.current = true
        try {
            const localFiles = fileList.filter(file => file.originFileObj)
            const existingIds = fileList
                .filter(file => !file.originFileObj)
                .map(file => Number(file.uid))
                .filter(id => !Number.isNaN(id))

            let uploaded: UploadedFile[] = []
            if (localFiles.length) {
                const result = await uploadFiles(localFiles)
                uploaded = result.data ?? []
                if (!uploaded.length) {
                    notification.error({ message: 'Не удалось сохранить превью бланка' })
                    return
                }
            }

            const nextIds = [...existingIds, ...uploaded.map(file => file.id)]
            await updateShipment({
                docId: shipmentDocId,
                body: {
                    data: {
                        //@ts-ignore
                        blankPreviewFiles: nextIds,
                    },
                },
            }).unwrap()

            const kept = blankPreviewFiles.filter(file => existingIds.includes(file.id))
            setBlankPreviewFiles([...kept, ...uploaded])
            dispatch(tasksApi.util.invalidateTags(['Tasks']))
        } finally {
            isSavingFileRef.current = false
        }
    }

    const onComplete = async () => {
        if (!formSet?.documentId) {
            notification.error({ message: 'Сначала создайте набор форм' })
            return
        }
        if (!forms.length) {
            notification.error({ message: 'Добавьте хотя бы одну форму в набор' })
            return
        }
        if (!isRework && !assembleFiles.length) {
            notification.error({ message: 'Загрузите файл сборки' })
            return
        }

        const res = isRework
            ? await completeRework(task.documentId)
            : await completePrep(task.documentId)

        if (!('error' in res) && res.data) {
            const correctorUsername = (
                res.data.task?.details as { correctorUsername?: string } | undefined
            )?.correctorUsername
            const baseMessage = isRework
                ? 'Правки отправлены корректору'
                : 'Сборка отправлена корректору'
            notification.success({
                message: correctorUsername
                    ? `${baseMessage}: ${correctorUsername}`
                    : baseMessage,
            })
            setOpen(false)
        }
    }

    const fileList: UploadFileInterface[] = assembleFiles.map(file => ({
        uid: String(file.id),
        name: file.name,
        status: 'done' as const,
        url: resolveUploadUrl(file.url),
    }))

    const blankFileList: UploadFileInterface[] = blankPreviewFiles.map(file => ({
        uid: String(file.id),
        name: file.name,
        status: 'done' as const,
        url: resolveUploadUrl(file.url),
    }))

    return (
        <>
            <Button
                type='primary'
                onClick={() => {
                    setBlankPreviewFiles(task.shipment?.blankPreviewFiles ?? [])
                    setOpen(true)
                }}
                disabled={task.taskStatus === 'Завершена'}
            >
                Завершить
            </Button>

            <Modal
                width={1000}
                title={isRework ? 'Внесение правок' : 'Подготовка сборки'}
                open={open}
                onCancel={() => setOpen(false)}
                destroyOnClose
                okText='Отправить корректору'
                onOk={onComplete}
                okButtonProps={{ loading: isLoading }}
                cancelText='Закрыть'
            >
                {isRework && task.description && (
                    <Alert
                        style={{ marginBottom: 16 }}
                        type='warning'
                        message='Комментарий корректора'
                        description={task.description}
                    />
                )}
                {isRework && !!task.attachments?.length && (
                    <Space direction='vertical' style={{ marginBottom: 16 }}>
                        {task.attachments.map(file => (
                            <a
                                key={file.documentId || file.id}
                                href={resolveUploadUrl(file.url)}
                                target='_blank'
                                rel='noreferrer'
                            >
                                {file.name || 'Вложение'}
                            </a>
                        ))}
                    </Space>
                )}

                <Title level={3}>Набор форм № {formSet?.id || ' - '}</Title>
                <Space style={{ width: '100%' }} direction='vertical' size={16}>
                    {formSet?.documentId ? (
                        <FormsTable
                            formSetDocId={formSet.documentId}
                            forms={forms}
                            hideOrderActions
                            onReorderForms={reorderForms}
                        />
                    ) : null}
                    <CreationForm formSet={formSet} onCreatedFormSet={linkFormSetToShipment} />
                </Space>

                <Divider />

                <Flex vertical gap={8}>
                    <div>
                        <b>Файл сборки</b>
                    </div>
                    <Upload
                        fileList={fileList}
                        beforeUpload={() => false}
                        multiple
                        disabled={!formSet?.documentId || isLoading}
                        onChange={({ fileList: nextList }) => {
                            void saveAssembleFiles(nextList)
                        }}
                    >
                        <Button disabled={!formSet?.documentId || isLoading}>
                            Загрузить файл сборки
                        </Button>
                    </Upload>
                    {!formSet?.documentId && (
                        <span style={{ color: '#999' }}>
                            Сначала добавьте форму — создастся набор.
                        </span>
                    )}
                </Flex>

                {isRework && (
                    <>
                        <Divider />
                        <Flex vertical gap={8}>
                            <div>
                                <b>Превью бланка</b>
                            </div>
                            <Upload
                                fileList={blankFileList}
                                beforeUpload={file => {
                                    const isPdf =
                                        file.type === 'application/pdf' ||
                                        /\.pdf$/i.test(file.name)
                                    if (!isPdf) {
                                        message.error('Допускаются только PDF-файлы')
                                        return Upload.LIST_IGNORE
                                    }
                                    return false
                                }}
                                multiple
                                accept='.pdf,application/pdf'
                                disabled={isLoading}
                                onChange={({ fileList: nextList }) => {
                                    void saveBlankPreviewFiles(nextList)
                                }}
                            >
                                <Button disabled={isLoading}>Загрузить превью бланка</Button>
                            </Upload>
                            {!!blankPreviewFiles.length && task.shipment && (
                                <Space size='small'>
                                    <PreviewShipmentBlank
                                        shipment={task.shipment}
                                        blankType='client'
                                        blankPreviewFiles={blankPreviewFiles}
                                    >
                                        <Button
                                            size='small'
                                            icon={<EyeOutlined />}
                                            color={BLANK_TYPE_BUTTON_COLOR.client}
                                            variant='solid'
                                            disabled={isLoading}
                                        >
                                            Клиентский
                                        </Button>
                                    </PreviewShipmentBlank>
                                    <PreviewShipmentBlank
                                        shipment={task.shipment}
                                        blankType='production'
                                        blankPreviewFiles={blankPreviewFiles}
                                    >
                                        <Button
                                            size='small'
                                            icon={<EyeOutlined />}
                                            color={BLANK_TYPE_BUTTON_COLOR.production}
                                            variant='solid'
                                            disabled={isLoading}
                                        >
                                            Произв.
                                        </Button>
                                    </PreviewShipmentBlank>
                                </Space>
                            )}
                        </Flex>
                    </>
                )}
            </Modal>
        </>
    )
}

export default CompleteAssemblyPrep
